// Pathname-based router for the TripzMini SPA.
// Replaces the previous hash-based routing.
//
// Route shapes:
//   /                       → { type: 'home' }
//   /trip/:slug             → { type: 'trip', slug }
//   /destination/:slug      → { type: 'destination', slug }
//   /about                  → { type: 'about' }     (premium-only, optional)
//   /pay/:token             → { type: 'pay', token } (operator-shared balance link)
//   anything else           → { type: 'unknown' }
//
// Listens to popstate so back/forward work, and exposes navigate() helpers.

import * as React from 'react'

export type Route =
  | { type: 'home' }
  | { type: 'trip'; slug: string }
  | { type: 'destination'; slug: string }
  | { type: 'about' }
  | { type: 'terms' }
  | { type: 'cancellation-policy' }
  | { type: 'pay'; token: string }
  | { type: 'unknown'; pathname: string }

export function parseRoute(pathname: string): Route {
  // Strip trailing slash (except root)
  const p = pathname.length > 1 && pathname.endsWith('/') ? pathname.slice(0, -1) : pathname
  if (p === '' || p === '/') return { type: 'home' }
  const tripMatch = p.match(/^\/trip\/([^/]+)$/)
  if (tripMatch) return { type: 'trip', slug: decodeURIComponent(tripMatch[1]) }
  const destMatch = p.match(/^\/destination\/([^/]+)$/)
  if (destMatch) return { type: 'destination', slug: decodeURIComponent(destMatch[1]) }
  // Operator-shared balance payment link. The token is the entire credential,
  // so it is matched loosely here and validated server-side on resolve.
  const payMatch = p.match(/^\/pay\/([^/]+)$/)
  if (payMatch) return { type: 'pay', token: decodeURIComponent(payMatch[1]) }
  if (p === '/about') return { type: 'about' }
  if (p === '/terms') return { type: 'terms' }
  if (p === '/cancellation-policy') return { type: 'cancellation-policy' }
  return { type: 'unknown', pathname: p }
}

export function buildPath(route: Route): string {
  switch (route.type) {
    case 'home': return '/'
    case 'trip': return `/trip/${encodeURIComponent(route.slug)}`
    case 'destination': return `/destination/${encodeURIComponent(route.slug)}`
    case 'about': return '/about'
    case 'terms': return '/terms'
    case 'cancellation-policy': return '/cancellation-policy'
    case 'pay': return `/pay/${encodeURIComponent(route.token)}`
    case 'unknown': return route.pathname
  }
}

export function navigate(route: Route, opts: { replace?: boolean } = {}) {
  // Preserve the query string (tenant `?t=` on the vercel/local host mode,
  // `?themePreview=` etc.) — same convention as the layouts' pushState calls.
  // Dropping it would lose the tenant on navigation and load the wrong site.
  const path = buildPath(route) + window.location.search
  if (opts.replace) {
    window.history.replaceState(null, '', path)
  } else {
    window.history.pushState(null, '', path)
  }
  // Dispatch a synthetic popstate so listeners react
  window.dispatchEvent(new PopStateEvent('popstate'))
}

export function useRoute(): Route {
  const [route, setRoute] = React.useState<Route>(() => parseRoute(window.location.pathname))
  React.useEffect(() => {
    const onPop = () => setRoute(parseRoute(window.location.pathname))
    window.addEventListener('popstate', onPop)
    return () => window.removeEventListener('popstate', onPop)
  }, [])
  return route
}
